import { userAutomationsService } from '@/services/user-automations.service';
import {
  AutomationStatus,
  UserAutomationsParams,
} from '@/types/automation.types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

// Query Keys
export const userAutomationKeys = {
  all: ['user-automations'] as const,
  list: (params: UserAutomationsParams) =>
    ['user-automations', 'list', params] as const,
};

// Main hook for user automations with pagination and filters
export const useUserAutomations = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<AutomationStatus>('all');
  const [templateFilter, setTemplateFilter] = useState<string>('all');
  const [domainFilter, setDomainFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  const params: UserAutomationsParams = {
    page: currentPage,
    limit: pageSize,
    ...(searchQuery && { search: searchQuery }),
    ...(statusFilter !== 'all' && { status: statusFilter }),
    ...(templateFilter !== 'all' && { templateId: templateFilter }),
    ...(domainFilter !== 'all' && { domainUuid: domainFilter }),
  };

  const {
    data: automationsData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: userAutomationKeys.list(params),
    queryFn: () => userAutomationsService.getUserAutomations(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Pagination helpers
  const totalPages = automationsData?.totalPages || 0;
  const totalItems = automationsData?.total || 0;
  const hasNext = automationsData?.hasNextPage || false;
  const hasPrev = automationsData?.hasPrevPage || false;

  const goToPage = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const nextPage = useCallback(() => {
    if (hasNext) {
      setCurrentPage(prev => prev + 1);
    }
  }, [hasNext]);

  const prevPage = useCallback(() => {
    if (hasPrev) {
      setCurrentPage(prev => Math.max(1, prev - 1));
    }
  }, [hasPrev]);

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
  }, []);

  const handleStatusChange = useCallback((status: AutomationStatus) => {
    setStatusFilter(status);
    setCurrentPage(1);
  }, []);

  const handleTemplateChange = useCallback((template: string) => {
    setTemplateFilter(template);
    setCurrentPage(1);
  }, []);

  const handleDomainChange = useCallback((domain: string) => {
    setDomainFilter(domain);
    setCurrentPage(1);
  }, []);

  return {
    // Data
    automations: automationsData?.data || [],
    totalPages,
    totalItems,
    currentPage,
    hasNext,
    hasPrev,
    isLoading,
    error,

    // Pagination
    goToPage,
    nextPage,
    prevPage,

    // Filters - état actuel
    searchQuery,
    statusFilter,
    templateFilter,
    domainFilter,

    // Filter setters
    setSearchQuery: handleSearchChange,
    setStatusFilter: handleStatusChange,
    setTemplateFilter: handleTemplateChange,
    setDomainFilter: handleDomainChange,

    // Actions
    refetch,
  };
};

// Hook to toggle automation status
export const useToggleAutomationStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      automationUuid,
      isActive,
    }: {
      automationUuid: string;
      isActive: boolean;
    }) => {
      return userAutomationsService.toggleAutomationStatus(
        automationUuid,
        isActive
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userAutomationKeys.all });
      toast.success('Automation status updated successfully');
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message || 'Failed to update automation status';
      toast.error(message);
    },
  });
};

// Hook to delete automation
export const useDeleteAutomation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (automationUuid: string) => {
      return userAutomationsService.deleteAutomation(automationUuid);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userAutomationKeys.all });
      toast.success('Automation deleted successfully');
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message || 'Failed to delete automation';
      toast.error(message);
    },
  });
};
